import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState, type FC } from 'react'

import { updateConversationSubjectByIdQueryClient } from 'api/conversations/setters'
import { usePendingConversationsQuery } from 'api/pendingConversations/queries'

import { useDebouncedSearchValue } from 'hooks/useDebouncedSearchValue'

import { useCompletedConversationToast } from './hooks/useCompletedConversationToast'

type PendingConversationIdsContextType = Set<string>
type PendingConversationsActionsContextType = {
  startPollingForPendingConversations: VoidFunction
}
export const PendingConversationIdsContext
  = createContext<PendingConversationIdsContextType>(new Set())

export const PendingConversationsActionsContext
  = createContext<PendingConversationsActionsContextType>({
    startPollingForPendingConversations: () => undefined,
  })

export const PendingConversationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [shouldPoll, setShouldPoll] = useState(true)

  const debouncedSearchValue = useDebouncedSearchValue()

  const { data: pendingConversationsData, isFetching, isRefetching, isSuccess, refetch } = usePendingConversationsQuery({ enabled: shouldPoll })

  const pendingConversations = useMemo(() => pendingConversationsData?.conversations || [], [pendingConversationsData])

  useCompletedConversationToast({ pendingConversations })

  useEffect(() => {
    if (!pendingConversations.length) return

    pendingConversations.forEach((conversation) => {
      if (conversation.subject) {
        updateConversationSubjectByIdQueryClient(
          conversation,
          debouncedSearchValue,
        )
      }
    })
  }, [debouncedSearchValue, pendingConversations])

  useEffect(() => {
    if (isFetching || isRefetching) return
    if (!isSuccess) return
    if (pendingConversations.length > 0) return
    if (!shouldPoll) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShouldPoll(false)
  }, [isFetching, isRefetching, isSuccess, pendingConversations, shouldPoll])

  const startPollingForPendingConversations = useCallback(() => {
    refetch()
    setShouldPoll(true)
  }, [refetch])

  const pendingConversationIds = useMemo(
    () => new Set(pendingConversations.map(conversation => conversation.id)),
    [pendingConversations],
  )

  const actions = useMemo(
    () => ({ startPollingForPendingConversations }),
    [startPollingForPendingConversations],
  )

  return (
    <PendingConversationsActionsContext.Provider value={actions}>
      <PendingConversationIdsContext.Provider value={pendingConversationIds}>
        {children}
      </PendingConversationIdsContext.Provider>
    </PendingConversationsActionsContext.Provider>
  )
}
